import type { PublicProductDetail } from '@repo/api-contracts';
import { ChevronRight } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router';
import { type SizeFieldValues, SizeTotalFields, sizeTotalsFromFields } from '@/entities/quote';
import { plainQuoteRequestPath, storeCategoryPath, storeCustomizePath } from '@/shared/config';
import { formatCount, formatKrw } from '@/shared/lib';
import { Button, buttonVariants, cn } from '@/shared/ui';
import { ColorSwatchPicker } from './color-swatch-picker';
import { CustomProductInquiry } from './custom-product-inquiry';
import { EstimatedTotal } from './estimated-total';
import { PrintGuideDialog } from './print-guide-dialog';
import { ProductReviewSummaryLink } from './product-review-summary-link';
import { ProductSectionTabs } from './product-section-tabs';
import { ProductionInfoTable } from './production-info-table';
import { QuantityStepper } from './quantity-stepper';

interface ProductInfoPanelProps {
  readonly detail: PublicProductDetail;
  readonly selectedColorId: string | null;
  readonly onSelectColor: (colorOptionId: string) => void;
  /** 수량 입력 문자열 — 페이지가 들고 있어 아래 수량별 가격 표의 강조와 같은 값을 쓴다. */
  readonly qtyDraft: string;
  readonly onChangeQtyDraft: (value: string) => void;
  /** 사이즈 상품의 사이즈별 수량 입력(빈칸 = 0) — 합계가 `qtyDraft` 가 되고, 커스터마이징·견적 요청으로 그대로 넘긴다. */
  readonly sizeDrafts: SizeFieldValues;
  readonly onChangeSizeDraft: (sizeOptionId: string, value: string) => void;
}

/**
 * 상세 우측 패널 — 위치(브레드크럼)·상품명·시작 가격, 바로가기 탭, 제작 정보 줄,
 * 인쇄 방식(칩을 누르면 프린트 가이드 모달)·색상, 수량 선택·예상 합계, 커스터마이징 CTA·상담 안내 순.
 */
export const ProductInfoPanel = ({
  detail,
  selectedColorId,
  onSelectColor,
  qtyDraft,
  onChangeQtyDraft,
  sizeDrafts,
  onChangeSizeDraft,
}: ProductInfoPanelProps): ReactElement => {
  const hasPriceTiers = detail.priceTiers.length > 0;
  const hasSizes = detail.sizeOptions.length > 0;
  // 사이즈별 수량 — 수량이 있는 사이즈만 다음 화면에 넘긴다(형식이 틀린 칸이 있으면 넘기지 않는다).
  const sizeTotals = hasSizes ? sizeTotalsFromFields(detail.sizeOptions, sizeDrafts) : null;
  const sizeQuantities =
    sizeTotals === null
      ? null
      : Object.fromEntries(
          sizeTotals.bySize
            .filter((row) => row.quantity > 0)
            .map((row) => [row.sizeOptionId, row.quantity]),
        );
  // 편집영역 없는 상품은 인쇄가 없어 인쇄 방식 줄·가이드를 숨기고, CTA 가 일반 견적 요청으로 간다.
  const showPrintMethods = detail.hasEditArea && detail.printMethodNames.length > 0;
  // 수량은 최소 주문 수량 이상의 정수일 때만 견적 요청에 싣는다.
  const draftQty = Number(qtyDraft);
  const parsedQty =
    qtyDraft.trim() !== '' && Number.isSafeInteger(draftQty) && draftQty >= detail.minOrderQty
      ? draftQty
      : null;
  // 프린트 가이드가 열려 있으면 누른 칩의 인쇄 방식 이름 — 가이드 안에서 그 방식을 강조한다.
  const [guideFor, setGuideFor] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-3">
        {detail.primaryCategory !== null && (
          // 대분류는 글자로만, 소분류는 그 분류로 좁힌 목록으로 돌아가는 링크.
          <nav aria-label="현재 위치">
            <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              {detail.primaryCategory.parentName !== null && (
                <li className="flex items-center gap-1">
                  {detail.primaryCategory.parentName}
                  <ChevronRight aria-hidden className="size-3.5" strokeWidth={1.5} />
                </li>
              )}
              <li>
                <Link
                  to={storeCategoryPath(detail.primaryCategory.id)}
                  className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {detail.primaryCategory.name}
                </Link>
              </li>
            </ol>
          </nav>
        )}

        <h1 className="text-[clamp(1.75rem,2.6vw,2.25rem)] leading-[1.2] font-bold tracking-[-0.02em] text-balance">
          {detail.name}
        </h1>

        {detail.baseUnitPrice !== null ? (
          <div className="space-y-1.5 pt-1">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-2xl font-bold tracking-[-0.01em] tabular-nums">
                {formatKrw(detail.baseUnitPrice)}부터
              </p>
              <span className="bg-brand-lime px-1.5 py-0.5 text-xs font-semibold text-black tabular-nums">
                최소 {formatCount(detail.minOrderQty)}개
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              부가세 포함 가격이며 수량에 따라 달라집니다
            </p>
            <ProductReviewSummaryLink productId={detail.id} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">가격 미정 (미발행 프리뷰)</p>
        )}
      </div>

      <div className="space-y-2 border-t border-foreground/10 pt-5">
        <ProductSectionTabs
          hasPriceTiers={hasPriceTiers}
          hasDescription={detail.detailImage !== null}
        />
        <ProductionInfoTable detail={detail} />

        {showPrintMethods && (
          <section
            aria-label="인쇄 방식"
            className="flex gap-4 border-t border-foreground/10 py-3 text-sm"
          >
            <h2 className="w-28 shrink-0 font-normal text-muted-foreground">인쇄 방식</h2>
            <ul className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {detail.printMethodNames.map((name) => (
                <li key={name}>
                  {/* 칩을 누르면 그 방식을 강조한 프린트 가이드가 뜬다. */}
                  <button
                    type="button"
                    aria-label={`${name} 인쇄 방식 안내`}
                    onClick={() => setGuideFor(name)}
                    className="rounded-full border border-foreground/15 px-2.5 py-0.5 text-xs transition-colors hover:border-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {name}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {showPrintMethods && guideFor !== null ? (
          <PrintGuideDialog
            focusName={guideFor}
            productMethodNames={detail.printMethodNames}
            onClose={() => setGuideFor(null)}
          />
        ) : null}
      </div>

      {detail.colorOptions.length > 0 && (
        <ColorSwatchPicker
          options={detail.colorOptions}
          selectedId={selectedColorId}
          onSelect={onSelectColor}
        />
      )}

      {/* 가격 구간이 없으면(미발행 프리뷰 등) 합계를 계산할 수 없어 수량 선택을 두지 않는다. 사이즈 상품은 사이즈별 수량이 곧 수량이다. */}
      {hasPriceTiers || hasSizes ? (
        <div className="space-y-4">
          {hasSizes ? (
            <div className="space-y-2">
              <SizeTotalFields
                options={detail.sizeOptions}
                values={sizeDrafts}
                onChange={onChangeSizeDraft}
              />
              <p className="text-sm text-muted-foreground">
                합계 {sizeTotals === null ? '—' : `${formatCount(sizeTotals.total)}개`} · 최소{' '}
                {formatCount(detail.minOrderQty)}개부터 주문할 수 있습니다. 배송지가 여러 곳이면
                견적 요청에서 나눌 수 있습니다.
              </p>
            </div>
          ) : (
            <QuantityStepper
              value={qtyDraft}
              minOrderQty={detail.minOrderQty}
              onChange={onChangeQtyDraft}
            />
          )}
          {hasPriceTiers ? (
            <EstimatedTotal
              tiers={detail.priceTiers}
              minOrderQty={detail.minOrderQty}
              qtyDraft={qtyDraft}
            />
          ) : null}
        </div>
      ) : null}

      <div className="space-y-3 border-t border-foreground/10 pt-6">
        {detail.hasEditArea ? (
          // 고른 색상을 에디터로 잇는다 — 에디터는 새 문서일 때만, 수급 가능하고 면 사진이 있을 때만 그 색상으로 시작한다.
          <Link
            to={storeCustomizePath(detail.id, selectedColorId, sizeQuantities)}
            className={cn(buttonVariants({ size: 'lg' }), 'h-14 w-full text-base')}
          >
            커스터마이징 시작
          </Link>
        ) : selectedColorId === null ? (
          // 공급 가능한 색상이 하나도 없으면 고를 색상이 없다 — 색상 없는 주소로 보내지 않고 막아 둔다.
          <Button size="lg" disabled className="h-14 w-full text-base">
            지금 견적을 요청할 수 있는 색상이 없습니다
          </Button>
        ) : (
          <Link
            to={plainQuoteRequestPath(detail.id, {
              colorOptionId: selectedColorId,
              quantity: parsedQty,
              sizes: sizeQuantities,
            })}
            className={cn(buttonVariants({ size: 'lg' }), 'h-14 w-full text-base')}
          >
            견적 요청
          </Link>
        )}

        <CustomProductInquiry />
      </div>
    </div>
  );
};
